import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'environments/environment';
import { BehaviorSubject, map, Observable, tap } from 'rxjs';
import {
  Bot,
  BotData,
  ChatbotCustomization,
  FollowUpConfiguration,
  Intent,
  PronunciationPreviewResult,
  Service,
  TransferWord,
  WelcomeSyncResult,
} from '../bots-crud/bots-crud.types';
import { BotSetup } from '@modules/settings/bot-setup/bot-setup.model';
import { OpCode } from '@modules/settings/bot-setup/op-codes/op-codes.types';
import { TagDefinition, TaggingRule, TaggingRulePayload } from 'app/interfaces/tagging';
@Injectable({ providedIn: 'root' })
export class BotCrudService {
  originalBot = {
    id: '74010810-16cf-441d-a1aa-c9fb620fceea',
    name: 'Voice Bot',
    createdBy: 'Franco',
    createdAt: 'November 01, 2021',
    modifiedAt: 'November 01, 2021',
    size: '3015 MB',
    type: 'folder',
    contents: '1 service, 2 intents',
    description: 'test voice bot',
    settings: {
      welcomeMessage: 'https://twilio-welcome-messages.s3.amazonaws.com/welcome-message.mp3',
      welcomeMessageMimeType: 'AUDIO_FILE',
      voice: {
        voiceLanguageCode: 'en-us',
        voiceName: 'Toyota',
        provider: {
          voiceUID: 'sK7ROW5x9jOZ1dHQ6Aja',
          provider: 'ELEVENLABS',
          providerBaseURL: 'https://api.elevenlabs.io/v1/text-to-speech/',
          providerKey: 'c870896e3ce89c3e1afa13f25ee35cff',
          voiceGender: 'FEMALE',
        },
      },
      messageCallbackProvider: {
        headers: {
          Authorization: 'Basic XXXX',
        },
        httpMethod: 'POST',
        url: 'http://localhost:3000/v1/say',
      },
    },
  };
  // Private
  public _items: BehaviorSubject<Bot[]> = new BehaviorSubject([]);

  /**
   * Constructor
   */
  /* eslint-disable @angular-eslint/prefer-inject */
  constructor(private _httpClient: HttpClient) {}
  /* eslint-enable @angular-eslint/prefer-inject */

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  /**
   * Getter for items
   */
  get items$(): Observable<Bot[]> {
    return this._items.asObservable();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  getBots(
    accountUID?: string,
    subAccountUID?: string,
    type?: string,
    page: number = 1,
    limit: number = 400
  ): Observable<Bot[]> {
    let params = new HttpParams();
    params = params.set('limit', String(limit));
    params = params.set('page', String(page));
    if (accountUID) {
      params = params.set('accountUID', accountUID);
    }
    if (subAccountUID) {
      params = params.set('subAccountUID', subAccountUID);
    }
    if (type) {
      params = params.set('type', type);
    }

    return this._httpClient
      .get<Bot[]>(`${environment.engineUrl}/bot`, { params })
      .pipe(map(bots => bots.map(bot => ({ ...bot, itemType: 'bot' }))));
  }

  // intent by UID
  getIntentById(intentId: string): Observable<Intent> {
    return this._httpClient.get<Intent>(`${environment.engineUrl}/intent/${intentId}`).pipe(
      map((elem) => {
        return { ...elem, itemType: 'intent' };
      })
    );
  }

  // all the intents of a bot
  getIntents(botId: string): Observable<Intent[]> {
    return this._httpClient
      .get<Intent[]>(`${environment.engineUrl}/intent/bot/${botId}`)
      .pipe(map(intents => intents.map(intent => ({ ...intent, itemType: 'intent' }))));
  }

  getServices(botServiceId: string): Observable<Service[]> {
    return this._httpClient
      .get<Service>(`${environment.engineUrl}/service/${botServiceId}`)
      .pipe(map(service => [{ ...service, itemType: 'service' }]));
  }

  /**
   * Create service
   *
   * @param requestBody
   */
  createService(requestBody: Partial<Service>): Observable<Service> {
    return this._httpClient.post<Service>(`${environment.engineUrl}/service`, requestBody);
  }

  /**
   * Edit service
   *
   * @param serviceData
   * @param uid
   */
  EditService(serviceData: Partial<Service>, uid: string): Observable<Service> {
    return this._httpClient.put<Service>(`${environment.engineUrl}/service/${uid}`, serviceData);
  }

  /**
   * Create bot
   *
   * @param bot
   */
  createBot(bot: BotData): Observable<BotData> {
    return this._httpClient.post<BotData>(`${environment.engineUrl}/bot`, bot);
  }

  /**
   * Get all bot templates
   */
  getBotTemplates(): Observable<Bot[]> {
    return this._httpClient
      .get<{ botTemplates: Bot[]; total: number }>(`${environment.engineUrl}/botTemplates`)
      .pipe(map(response => response.botTemplates));
  }

  /**
   * Get bot template by UID
   *
   * @param uid
   */
  getBotTemplateById(uid: string): Observable<BotData> {
    return this._httpClient
      .get<{ bot: BotData }>(`${environment.engineUrl}/botTemplates/${uid}`)
      .pipe(map(response => response.bot));
  }

  /**
   * Edit bot
   * @param editData
   * @param uid
   */
  editBot(uid: string, editData: Partial<BotData>): Observable<BotData> {
    return this._httpClient.put<BotData>(`${environment.engineUrl}/bot/${uid}`, editData);
  }

  getRepairCostEstimateStatus(botId: string): Observable<{ enabled: boolean; intentUID?: string }> {
    return this._httpClient.get<{ enabled: boolean; intentUID?: string }>(
      `${environment.engineUrl}/bot/${botId}/repair-cost-estimate`
    );
  }

  enableRepairCostEstimate(botId: string): Observable<{ enabled: boolean; intentUID: string; bot: BotData }> {
    return this._httpClient.post<{ enabled: boolean; intentUID: string; bot: BotData }>(
      `${environment.engineUrl}/bot/${botId}/repair-cost-estimate/enable`,
      {}
    );
  }

  disableRepairCostEstimate(botId: string): Observable<{ enabled: boolean; bot: BotData }> {
    return this._httpClient.post<{ enabled: boolean; bot: BotData }>(
      `${environment.engineUrl}/bot/${botId}/repair-cost-estimate/disable`,
      {}
    );
  }

  getFinancingEstimateStatus(botId: string): Observable<{ enabled: boolean }> {
    return this._httpClient.get<{ enabled: boolean }>(`${environment.engineUrl}/bot/${botId}/financing-estimate`);
  }

  enableFinancingEstimate(botId: string): Observable<{ enabled: boolean; bot: BotData }> {
    return this._httpClient.post<{ enabled: boolean; bot: BotData }>(
      `${environment.engineUrl}/bot/${botId}/financing-estimate/enable`,
      {}
    );
  }

  disableFinancingEstimate(botId: string): Observable<{ enabled: boolean; bot: BotData }> {
    return this._httpClient.post<{ enabled: boolean; bot: BotData }>(
      `${environment.engineUrl}/bot/${botId}/financing-estimate/disable`,
      {}
    );
  }

  updateBotAllowedDomains(bot: BotData, cors: string[]): Observable<BotData> {
    const payload: Partial<BotData> = {
      ...bot,
      settings: {
        ...bot.settings,
        channel: {
          ...bot.settings?.channel,
          chatbot: {
            ...bot.settings?.channel?.chatbot,
            cors,
          },
        },
      },
    };

    return this.editBot(bot._id!, payload);
  }

  /**
   * Create bot
   * @param bot
   */
  createOnboardingBots(bot: BotSetup): Observable<BotSetup> {
    return this._httpClient.post<BotSetup>(`${environment.engineUrl}/onboarding`, bot);
  }

  /**
   * Update bot
   * @param botId
   * @param bot
   */
  updateOnboardingBots(botId: string, bot: BotSetup): Observable<BotSetup> {
    return this._httpClient.put<BotSetup>(`${environment.engineUrl}/onboarding/${botId}`, bot);
  }

  /**
   * Get Sms Origin
   * @param accountUID
   * @param subAccountUID
   */
  getSmsOrigin(accountUID: string, subAccountUID: string): Observable<{ phoneNumber: string }> {
    const module = 'VA';
    const url = `${environment.engineUrl}/onboarding/sms/origin/${accountUID}/${subAccountUID}/${module}`;

    return this._httpClient.get<{ origin: string; error?: string }>(url).pipe(
      map((resp) => {
        if (resp?.error) {
          throw new Error(resp.error);
        }
        return { phoneNumber: resp.origin };
      })
    );
  }

  getWelcomeMessageAudio(
    text: string,
    audioType: string = 'mp3_44100_128',
    voice?: string,
    promptTemplate?: string
  ): Observable<Blob> {
    let params = new HttpParams().set('audioType', audioType).set('text', text);

    if (voice) {
      params = promptTemplate ? params.set('voice', voice) : params.set('voiceUID', voice);
    }
    if (promptTemplate) {
      params = params.set('promptTemplate', promptTemplate);
    }

    const headers = new HttpHeaders({
      'x-content-type': 'audio/mpeg',
    });

    return this._httpClient.get(`${environment.engineUrl}/welcome-message`, {
      params,
      headers,
      responseType: 'blob',
    });
  }

  /**
   * Translates welcome-message templates into `targetLanguage` via ai-engine's
   * LLM-backed /language/translate endpoint — used to resolve onboarding's
   * default welcome text for a language that has no hand-written entry in
   * bot-setup's defaultWelcomeTemplatesByTypeAndLanguage map (e.g. the
   * AIVA2-only languages: Hindi, Farsi, Cantonese). Each `{id, text}` keeps
   * the {BOT_NAME} placeholder token intact — substitution happens after
   * translation, same as the hand-written templates.
   */
  translateWelcomeTemplates(
    messages: { id: string; text: string }[],
    targetLanguage: string
  ): Observable<{ messages: { id: string; translatedText: string }[] }> {
    return this._httpClient.post<{ messages: { id: string; translatedText: string }[] }>(
      `${environment.engineUrl}/language/translate`,
      { messages, targetLanguage }
    );
  }

  listVoiceFlavorPresets(): Observable<
    Array<{
      id: string;
      displayName: string;
      description: string;
      voiceType: string;
      voiceGender: string;
      previewVoice: string;
      previewPrompt: string;
      defaultAttributes: {
        tone: number;
        friendliness: number;
        professionalism: number;
      };
    }>
  > {
    return this._httpClient.get<
      Array<{
        id: string;
        displayName: string;
        description: string;
        voiceType: string;
        voiceGender: string;
        previewVoice: string;
        previewPrompt: string;
        defaultAttributes: {
          tone: number;
          friendliness: number;
          professionalism: number;
        };
      }>
    >(`${environment.engineUrl}/voice-flavor`);
  }

  /**
   * Render a pronunciation preview clip using the bot's own TTS provider.
   * The server caches the clip on S3 and returns its URL plus a voice
   * signature; passing the cached values back lets an unchanged word reuse
   * the existing clip instead of re-rendering.
   */
  getPronunciationPreview(
    botId: string,
    body: {
      spoken: string;
      language?: string;
      display?: string;
      cachedVoiceSignature?: string;
      cachedPreviewAudioUrl?: string;
      cachedPreviewAudioKey?: string;
    }
  ): Observable<PronunciationPreviewResult> {
    return this._httpClient.post<PronunciationPreviewResult>(
      `${environment.engineUrl}/voice-bot/${botId}/pronunciation/preview`,
      body
    );
  }

  /**
   * Check whether the bot's pre-rendered welcome audio is still in sync with the
   * pronunciation dictionary. Pass the live form dictionary + welcome texts so
   * the answer reflects unsaved edits; the server compares signatures and
   * returns, per greeting, whether it is stale, which dictionary words are baked
   * in, and the dictionary-applied spoken text (for a dictionary-aware preview).
   */
  getWelcomeSyncStatus(
    botId: string,
    body: {
      items?: { type: string; plainText: string }[];
      pronunciationOverrides?: Array<{
        display: string;
        variants?: Array<{ language: string; spoken: string }>;
      }>;
    }
  ): Observable<WelcomeSyncResult> {
    return this._httpClient.post<WelcomeSyncResult>(`${environment.engineUrl}/voice-bot/${botId}/welcome-sync`, body);
  }

  /** AIVA-1856: get a bot's configured transfer word bank. */
  getTransferWords(botId: string): Observable<TransferWord[]> {
    return this._httpClient.get<TransferWord[]>(`${environment.engineUrl}/bot/${botId}/transfer-words`);
  }

  /** AIVA-1856: replace a bot's transfer word bank. */
  updateTransferWords(botId: string, words: TransferWord[]): Observable<TransferWord[]> {
    return this._httpClient.put<TransferWord[]>(`${environment.engineUrl}/bot/${botId}/transfer-words`, { words });
  }

  /** AIVA-1856: bulk-add words (e.g. from a client-parsed CSV) to the existing bank. */
  importTransferWords(botId: string, words: string[]): Observable<TransferWord[]> {
    return this._httpClient.post<TransferWord[]>(`${environment.engineUrl}/bot/${botId}/transfer-words/import`, {
      words,
    });
  }

  /**
   * Get Chatbot Customization
   *
   * @param uid
   */
  getChatbotCustomization(uid: string): Observable<ChatbotCustomization> {
    return this._httpClient.get<ChatbotCustomization>(`${environment.chatEngineUrl}/chatbot-config/${uid}`).pipe(
      map((response) => {
        return response;
      })
    );
  }

  /**
   * Update Chatbot Customization
   *
   * @param uid
   */
  updateChatbotCustomization(uid: string, payload: Partial<ChatbotCustomization>): Observable<ChatbotCustomization> {
    return this._httpClient.patch<ChatbotCustomization>(`${environment.chatEngineUrl}/chatbot-config/${uid}`, payload);
  }

  getBotsByType(type: string): Observable<Bot[]> {
    let params = new HttpParams();
    params = params.set('type', type);
    params = params.set('deleted', 'false');

    return this._httpClient.get<Bot[]>(`${environment.kenilityEngineUrl}/bot`, { params }).pipe(
      tap(bots => console.warn(`API response - getBotsByType(${type}):`, bots)),
      map(bots => bots.map(bot => ({ ...bot, itemType: 'bot' })))
    );
  }

  getBotsByName(displayName: string): Observable<Bot[]> {
    let params = new HttpParams();
    params = params.set('displayName', displayName);
    params = params.set('deleted', 'false');

    return this._httpClient.get<Bot[]>(`${environment.kenilityEngineUrl}/bot`, { params }).pipe(
      tap(bots => console.warn(`API response - getBotsByName(${displayName}):`, bots)),
      map(bots => bots.map(bot => ({ ...bot, itemType: 'bot' })))
    );
  }

  /**
   * Get Follow-Up Configuration by bot identifier (Voice bot _id)
   *
   * @param identifier - The _id of the Voice bot
   */
  getFollowUpConfiguration(identifier: string): Observable<FollowUpConfiguration[]> {
    return this._httpClient.get<FollowUpConfiguration[]>(
      `${environment.engineUrl}/follow-up-configuration/identifier/${identifier}`
    );
  }

  /**
   * Get Service Operations (OP Codes) by subscription ID
   *
   * @param subscriptionId
   * @param isEditMode
   */
  getServiceOperations(subscriptionId: string, isEditMode: boolean = false): Observable<OpCode[]> {
    let params = new HttpParams();
    params = params.set('subscriptionId', subscriptionId);
    params = params.set('integrationType', 'fortellis');

    if (isEditMode) {
      params = params.set('internalConfiguration', 'true');
    }

    return this._httpClient
      .get<OpCode[]>(`${environment.engineUrl}/service-operation`, { params })
      .pipe(map(opCodes => opCodes || []));
  }

  /**
   * AIVA-1780: unified read-only opcode review (API-sourced + manually created).
   */
  getServiceOperationsForReview(
    subscriptionId: string,
    filters?: { source?: 'api' | 'manual' | 'all'; active?: boolean }
  ): Observable<OpCode[]> {
    let params = new HttpParams();
    params = params.set('subscriptionId', subscriptionId);
    params = params.set('integrationType', 'fortellis');

    if (filters?.source && filters.source !== 'all') {
      params = params.set('source', filters.source);
    }
    if (typeof filters?.active === 'boolean') {
      params = params.set('active', String(filters.active));
    }

    return this._httpClient
      .get<OpCode[]>(`${environment.engineUrl}/service-operation/review`, { params })
      .pipe(map(opCodes => opCodes || []));
  }

  /**
   * Update Service Operations (OP Codes)
   *
   * @param opCodes
   * @param subscriptionId
   * @param isEditMode
   */
  updateServiceOperations(opCodes: OpCode[], subscriptionId: string, isEditMode: boolean = false): Observable<OpCode[]> {
    let params = new HttpParams();
    params = params.set('subscriptionId', subscriptionId);
    params = params.set('integrationType', 'fortellis');

    if (isEditMode) {
      params = params.set('internalConfiguration', 'true');
    }

    return this._httpClient.post<OpCode[]>(`${environment.engineUrl}/service-operation`, opCodes, { params });
  }

  /**
   * Delete Service Operation (OP Code)
   *
   * @param subscriptionId
   * @param opCodeId
   */
  deleteServiceOperation(subscriptionId: string, opCodeId: string): Observable<void> {
    let params = new HttpParams();
    params = params.set('subscriptionId', subscriptionId);
    params = params.set('id', opCodeId);
    params = params.set('integrationType', 'fortellis');

    return this._httpClient.delete<void>(`${environment.engineUrl}/service-operation`, { params });
  }

  getTagDefinitions(params?: {
    provider?: string;
    entityType?: string;
    allowManual?: boolean;
  }): Observable<TagDefinition[]> {
    let httpParams = new HttpParams();

    if (params?.provider) {
      httpParams = httpParams.set('provider', params.provider);
    }

    if (params?.entityType) {
      httpParams = httpParams.set('entityType[]', params.entityType);
    }

    if (typeof params?.allowManual === 'boolean') {
      httpParams = httpParams.set('allowManual', String(params.allowManual));
    }

    return this._httpClient.get<TagDefinition[]>(`${environment.interactionTrackingApiUrl}/tag-definitions`, {
      params: httpParams,
    });
  }

  getTaggingRules(params: { provider: string; entityType: 'BOT'; entityUID: string }): Observable<TaggingRule[]> {
    const httpParams = new HttpParams()
      .set('provider', params.provider)
      .set('entityType', params.entityType)
      .set('entityUID', params.entityUID);

    return this._httpClient.get<TaggingRule[]>(`${environment.interactionTrackingApiUrl}/tagging-rules`, {
      params: httpParams,
    });
  }

  createTaggingRule(payload: TaggingRulePayload): Observable<TaggingRule> {
    return this._httpClient.post<TaggingRule>(`${environment.interactionTrackingApiUrl}/tagging-rules`, payload);
  }

  deleteTaggingRule(ruleId: string): Observable<void> {
    return this._httpClient.delete<void>(`${environment.interactionTrackingApiUrl}/tagging-rules/${ruleId}`);
  }
}
